import { getRpc } from '../utils/initEditorSdk';
import { Watermark } from '../utils/watermark';
import { RPC_CONTEXT } from '@cloudstudio/editor-sdk';
import { useEffect, useRef, useState } from 'preact/hooks';
import httpClient from '../utils/httpClient';
import { setDisableExtensions } from '../utils/vsbridge';
import { setPublicConfig } from '../utils/config';
import { initializeAegis, logWorkSpaceSpeed } from '../routes/home/gaLog';
import urlUtils from '../utils/urlUtils';
import {
  getAppParams,
  getChannelSource,
  getRegionUrl,
  getReportParams,
  onCheckHaiAuthorization,
  onResHandler,
  retry,
} from '../components/app.util';
import { haiAuthorizationUrl } from '../constants/app';
import {
  onCheckPing,
  onSignToken,
  onStartHaiInstance,
  onStatusCheck,
  onSynHaiEnter,
  queryHaiBySpaceKey,
  queryHaiStatus,
  runWorkspace,
} from '../services/app';
import {
  HaiInfo,
  HaiStatus,
  PingStatus,
  onPostRegionUrlParams,
  onSignTokenRes,
  onSynHaiEnterRes,
} from '../components/app.type';
import useRefresh from './useParamsRefresh';
import { HaiStatusEnum } from '../components/app.constants';

// 安全增强
async function doSecret({ content }) {
  const w = new Watermark({
    container: window.parent.document.body,
    content,
  });
  w.observe(() => {
    window.parent.location.reload();
  });
  try {
    const rpc = await getRpc();
    const cmdService = rpc.getProxy(RPC_CONTEXT.CommandService);
    await Promise.all([
      // 禁用下载
      cmdService.executeCommand('cloudstudio.disableDownload'),
      // 复制加密
      cmdService.executeCommand('cloudstudio.setCryptoKey', content),
    ]);
  } catch (err) {
    console.error(err);
  }
}
function useInitialApp() {
  const spaceKeyRef = useRef<string>(urlUtils.getSpaceKey() || '');
  const [config, setConfig] = useState({});
  const [postRegionUrlParams, setPostRegionUrlParams] = useState<Partial<onPostRegionUrlParams>>({});
  const [errorMsg, setErrorMsg] = useState('');
  const customerReport = {
    open: false,
    onError: (error) => {
      return Promise.reject(error);
    },
  };
  const { promise: onCheckPingPromise, cancel } = retry(
    async () => {
      const res = await onResHandler<PingStatus>(await onCheckPing(spaceKeyRef.current), customerReport);
      if (res?.ready) {
        logWorkSpaceSpeed('hai', 'pingSuccess');
        return true;
      }
      throw new Error(`ping error or timeout`);
    },
    5 * 30,
    200
  );
  const { promise: onCheckHaiStatus, cancel: cancelHaiStatus } = retry(
    async () => {
      const res = await onResHandler<HaiStatus>(await queryHaiStatus(spaceKeyRef.current), customerReport);
      if (res?.status === HaiStatusEnum.running) {
        return true;
      }
      throw new Error(`hai status error or timeout`);
    },
    60 * 10,
    1000
  );
  useRefresh();

  const getPublicConfig = async () => {
    const res = await httpClient({
      path: `/public/config`,
      method: 'GET',
    });
    if (!res || res.code !== 0 || !res.data) {
      return;
    }
    setPublicConfig(res.data);
    return res.data;
  };

  const userInfoPromise = () =>
    httpClient({
      path: '/user/info',
      method: 'GET',
    });

  const onNormalInit = () => {
    setDisableExtensions();
    async function fetch() {
      const configData = await getPublicConfig();
      initializeAegis(configData);
      const [res2, res3, res4] = await Promise.all([
        httpClient({
          path: `/workspace/${spaceKeyRef?.current}/share/partners/authorization`,
          method: 'GET',
        }),
        userInfoPromise(),
        onStatusCheck(spaceKeyRef.current),
      ]);
      // 检查是否是分享模式的参与者
      if (res2.code === 0) {
        configData.isPartner = true;
      }
      configData.userInfo = res3;
      configData.spec = res4.data?.spec;
      setConfig(configData);
      if (configData.secretEnabled && urlUtils.isEmbeddedLoader()) {
        const username = res3.data?.authenticationUserInfo.nickName || urlUtils.getCookie('cloudstudio-name');
        const id = res3.data?.id || 'unknown';
        const content = `${id}(${username})`;
        doSecret({ content });
      }
    }
    fetch();
  };

  const onHandlerUnAuth = async () => {
    const { code, msg } = getAppParams();
    if (code && msg) {
      setErrorMsg(msg);
      throw new Error(`${code} ${msg}`);
    }
  };

  const initHaiAuthAndReportConfig = async () => {
    await onHandlerUnAuth();
    await handleCheckHaiAuthorization();
    const configData = await getPublicConfig();
    const userInfo = await userInfoPromise();
    configData.userInfo = userInfo;
    setConfig(configData);
  };

  const handleCheckHaiAuthorization = async () => {
    const res = await onCheckHaiAuthorization();
    if (!res?.granted) {
      location.href = haiAuthorizationUrl;
      return;
    }
  };

  const handleSynHaiEnter = async () => {
    const { region, instanceId } = getAppParams();
    const params = getReportParams();
    try {
      const syncRes = await onSynHaiEnter({ instanceId, region });
      const { spaceKey } = await onResHandler<onSynHaiEnterRes>(syncRes, params);
      if (spaceKey) {
        spaceKeyRef.current = spaceKey;
      }
    } catch (error) {
      throw new Error(error?.message || '通过instanceId获取spaceKey失败');
    }
  };

  // hai channel, start之前已经通过instanceId获取对应spaceKey了
  const startHai = async () => {
    const params = getReportParams();
    await onResHandler(await onStartHaiInstance({ spaceKey: spaceKeyRef.current }), params);
    const haiStatusParams = getReportParams();
    try {
      await onCheckHaiStatus();
    } catch (error) {
      onResHandler(error, haiStatusParams);
      setErrorMsg('hai status error or timeout');
      throw new Error('hai status error or timeout');
    }
  };

  const startWs = async () => {
    await runWorkspace(spaceKeyRef.current);
  };

  const handlePing = async () => {
    const params = getReportParams();
    try {
      await onCheckPingPromise();
    } catch (error) {
      onResHandler(error, params);
      setErrorMsg('ping hai timeout');
      throw new Error('ping hai timeout');
    }
  };

  const handleSignToken = async () => {
    const params = getReportParams();
    const { token } = await onResHandler<onSignTokenRes>(await onSignToken(spaceKeyRef.current), params);
    const { region } = getAppParams();
    let synRegion = region;
    // token illegal 302 case, need query hai info by spaceKey
    if (!synRegion) {
      const params = getReportParams();
      synRegion = (await onResHandler<HaiInfo>(await queryHaiBySpaceKey(spaceKeyRef.current), params))?.region;
    }
    const regionUrl = `${getRegionUrl({ spaceKey: spaceKeyRef.current, region: synRegion })}`;
    setPostRegionUrlParams({ token, url: regionUrl });
  };

  const handleStartAndGetToken = async () => {
    try {
      await Promise.all([startHai(), startWs()]);
      await handlePing();
      await handleSignToken();
    } catch (error) {
      setErrorMsg(error?.message);
    }
  };

  const onHaiOriginCsInit = async () => {
    try {
      await initHaiAuthAndReportConfig();
      await handleStartAndGetToken();
    } catch (error) {
      setErrorMsg(error.message);
    }
  };

  const onHaiOriginHaiInit = async () => {
    try {
      await initHaiAuthAndReportConfig();
      await handleSynHaiEnter();
      await handleStartAndGetToken();
    } catch (error) {
      setErrorMsg(error.message);
    }
  };

  useEffect(() => {
    const { isNormal, isHaiOriginCs, isHaiOriginHai } = getChannelSource();
    if (isNormal) {
      onNormalInit();
    }
    if (isHaiOriginCs) {
      onHaiOriginCsInit();
    }
    if (isHaiOriginHai) {
      onHaiOriginHaiInit();
    }

    return () => {
      cancel();
      cancelHaiStatus();
    };
  }, []);

  return [config, postRegionUrlParams, errorMsg];
}

export default useInitialApp;
